import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { UpdateOrderItemDiscountModalProps } from "../interfaces/modals";
import { useUpdateOrderItemDiscount } from "../../../hooks/api/useSalesOrder";
import { isOrderMutationRejectedError } from "../../../hooks/api/orderMutationErrors";
import { messageForOrderMutationCode } from "../utils/orderErrorMessages";
import { orderItemLineLabel } from "../utils/orderUtils";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";

type DiscountMode = "PERCENTAGE" | "FIXED_AMOUNT";

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000;
}

const UpdateOrderItemDiscountModal: React.FC<
  UpdateOrderItemDiscountModalProps
> = ({ open, onClose, orderId, line, onUpdated }) => {
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();
  const updateOrderItemDiscount = useUpdateOrderItemDiscount({
    suppressErrorBanner: true
  });

  const [mode, setMode] = useState<DiscountMode>("PERCENTAGE");
  const [inputValue, setInputValue] = useState("0");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !line) {
      setMode("PERCENTAGE");
      setInputValue("0");
      setSubmitError(null);
      return;
    }
    if ((line.discountPerUnit ?? 0) > 0) {
      setMode("FIXED_AMOUNT");
      setInputValue(String(line.discountPerUnit));
    } else {
      setMode("PERCENTAGE");
      setInputValue("0");
    }
    setSubmitError(null);
  }, [open, line]);

  const parsedValue = useMemo(() => {
    const normalized = inputValue.replace(",", ".").trim();
    if (!normalized) return NaN;
    return Number(normalized);
  }, [inputValue]);

  const preview = useMemo(() => {
    if (!line) {
      return {
        discountPerUnit: 0,
        discountAmount: 0,
        discountedUnitPrice: 0,
        lineTotal: 0
      };
    }
    const rawValue = Number.isFinite(parsedValue) ? parsedValue : 0;
    const normalizedValue = Math.max(0, rawValue);
    const discountPerUnit =
      mode === "PERCENTAGE"
        ? roundMoney((line.unitPrice * normalizedValue) / 100)
        : roundMoney(normalizedValue);
    const boundedDiscountPerUnit = Math.min(discountPerUnit, line.unitPrice);
    const discountAmount = roundMoney(boundedDiscountPerUnit * line.quantity);
    const discountedUnitPrice = roundMoney(
      line.unitPrice - boundedDiscountPerUnit
    );
    const lineTotal = roundMoney(discountedUnitPrice * line.quantity);

    return {
      discountPerUnit: boundedDiscountPerUnit,
      discountAmount,
      discountedUnitPrice,
      lineTotal
    };
  }, [line, mode, parsedValue]);

  const handleSubmit = async () => {
    if (!line) return;
    setSubmitError(null);

    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      setSubmitError(
        t("order-line-discount-invalid-value", "Enter a valid discount value.")
      );
      return;
    }

    if (mode === "PERCENTAGE" && parsedValue > 100) {
      setSubmitError(
        t(
          "order-line-discount-percent-too-high",
          "Percentage discount cannot exceed 100%."
        )
      );
      return;
    }

    if (
      mode === "FIXED_AMOUNT" &&
      line.unitPrice > 0 &&
      parsedValue > line.unitPrice
    ) {
      setSubmitError(
        t(
          "order-line-discount-fixed-too-high",
          "Discount per unit cannot exceed the unit price."
        )
      );
      return;
    }

    try {
      const order = await updateOrderItemDiscount.mutateAsync({
        orderId,
        productItemId: line.itemId,
        discountPerUnit: preview.discountPerUnit
      });
      if (order) {
        await onUpdated();
        onClose();
      } else {
        setSubmitError(
          t(
            "order-line-discount-save-failed",
            "Could not save this discount. Please try again."
          )
        );
      }
    } catch (error) {
      if (isOrderMutationRejectedError(error)) {
        setSubmitError(messageForOrderMutationCode(error.apiCode, t));
      } else {
        setSubmitError(
          t(
            "order-line-discount-unexpected-error",
            "Something went wrong while saving the discount."
          )
        );
      }
    }
  };

  const submitBusy = updateOrderItemDiscount.isPending;
  const lineLabel = line ? orderItemLineLabel(line) : "";

  return (
    <Modal open={open} onClose={submitBusy ? undefined : onClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("order-line-discount-title", "Update line discount")}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={submitBusy}>
            <CloseIcon />
          </IconButton>
        </Box>

        {line && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {lineLabel}
          </Typography>
        )}

        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            select
            label={t("order-line-discount-type", "Discount type")}
            value={mode}
            size="small"
            fullWidth
            disabled={submitBusy}
            onChange={(event) => {
              setMode(event.target.value as DiscountMode);
              setSubmitError(null);
            }}
          >
            <MenuItem value="PERCENTAGE">
              {t("order-line-discount-type-percent", "Percentage")}
            </MenuItem>
            <MenuItem value="FIXED_AMOUNT">
              {t("order-line-discount-type-fixed", "Exact amount")}
            </MenuItem>
          </TextField>

          <TextField
            label={
              mode === "PERCENTAGE"
                ? t("order-line-discount-percent", "Discount (%)")
                : t("order-line-discount-fixed", "Discount per unit")
            }
            type="number"
            value={inputValue}
            size="small"
            fullWidth
            disabled={submitBusy}
            onChange={(event) => {
              setInputValue(event.target.value);
              setSubmitError(null);
            }}
            inputProps={{
              min: 0,
              max: mode === "PERCENTAGE" ? 100 : (line?.unitPrice ?? undefined),
              step: mode === "PERCENTAGE" ? 0.01 : 0.0001
            }}
            helperText={
              mode === "PERCENTAGE"
                ? t(
                    "order-line-discount-percent-helper",
                    "Enter a value between 0 and 100."
                  )
                : t(
                    "order-line-discount-fixed-helper",
                    "This amount is applied to each unit."
                  )
            }
          />

          {line && (
            <Stack
              spacing={1}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.default"
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("unit-price", "Unit Price")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(line.unitPrice)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("qty", "Qty")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {line.quantity}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "order-line-discount-per-unit-preview",
                    "Discount per unit"
                  )}
                  :
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(preview.discountPerUnit)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("order-line-discount-total-preview", "Discount total")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(preview.discountAmount)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "order-line-discounted-unit-price",
                    "Discounted unit price"
                  )}
                  :
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(preview.discountedUnitPrice)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("line-total", "Line Total")}:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(preview.lineTotal)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            flexWrap: "wrap"
          }}
        >
          <LoadingButton
            variant="outlined"
            onClick={onClose}
            disabled={submitBusy}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              borderColor: BRAND_COLOR,
              "&:hover": {
                borderColor: BRAND_HOVER,
                backgroundColor: BRAND_HOVER,
                color: "white"
              }
            }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            disableElevation
            onClick={() => void handleSubmit()}
            loading={submitBusy}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save", "Save")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateOrderItemDiscountModal;
